import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FounderNav } from '../components/FounderNav';
import { fetchFounderFlags, setFounderFlag, type SystemFlag } from '../lib/api';
import { confirmDialog } from '../lib/dialog';

/**
 * Founder feature-flag surface. Lists every flag with a one-tap
 * toggle plus a "create flag" form for new keys. Defaults live in
 * the code that reads them (passed as the second arg to getFlag) —
 * flipping a flag here is the override.
 *
 * The three well-known kill switches code already reads:
 *
 *   - signups_open                — pauses new memberships
 *   - matching_enabled            — pauses the weekly + on-signup
 *                                   matcher
 *   - outbound_email_enabled      — sendEmail logs only, no Resend
 *
 * Defaults are all "true" (system normal). Flipping to false is the
 * emergency stop. The in-process flag cache is busted on every
 * write, so the running API picks up the new value within one read.
 */
const WELL_KNOWN_FLAGS = [
  {
    key: 'signups_open',
    description:
      'Allow new students to complete onboarding (creates a membership). OFF = signups paused.',
  },
  {
    key: 'matching_enabled',
    description:
      'Allow the weekly rematch scheduler + on-signup matcher to pair members. OFF = no new pairings.',
  },
  {
    key: 'outbound_email_enabled',
    description:
      'Send transactional emails via Resend. OFF = emails are logged but not delivered (dry-run).',
  },
];

export function FounderFlags() {
  const [flags, setFlags] = useState<SystemFlag[] | null>(null);
  const [stale, setStale] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [newKey, setNewKey] = useState('');
  const [newDescription, setNewDescription] = useState('');

  function load() {
    fetchFounderFlags().then((res) => {
      if (res.ok) setFlags(res.data.flags);
      else if (res.stale) setStale(true);
      else setError(res.error);
    });
  }

  useEffect(load, []);

  async function toggle(key: string, currentValue: boolean, description: string | null) {
    if (pending) return;
    const next = !currentValue;
    const ok = await confirmDialog({
      title: `${next ? 'Enable' : 'Disable'} "${key}"?`,
      message: `${description ?? ''}\n\nThe change takes effect within ~5 seconds across the running API.`,
      confirmLabel: next ? 'Enable' : 'Disable',
      danger: !next,
    });
    if (!ok) return;
    setPending(key);
    setError(null);
    const result = await setFounderFlag(key, next, description ?? undefined);
    setPending(null);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    load();
  }

  async function create() {
    const k = newKey.trim();
    if (!k.match(/^[a-z][a-z0-9_]{2,60}$/)) {
      setError('Key must be lowercase snake_case, 3-60 chars (e.g., enable_new_feature).');
      return;
    }
    setPending(k);
    const result = await setFounderFlag(k, true, newDescription.trim() || undefined);
    setPending(null);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setNewKey('');
    setNewDescription('');
    load();
  }

  if (stale) {
    return (
      <main className="wide">
        <div className="card">
          <h1>Session is too old</h1>
          <p className="muted">Founder writes require a fresh sign-in.</p>
          <p>
            <Link to="/">← Home</Link>
          </p>
        </div>
      </main>
    );
  }

  // Merge well-known flags (with their defaults) over whatever exists
  // in the DB. Anything in the DB that isn't well-known is still shown
  // — custom flags created by the founder show up at the bottom.
  const knownKeys = new Set(WELL_KNOWN_FLAGS.map((f) => f.key));
  const dbByKey = new Map(flags?.map((f) => [f.key, f]) ?? []);
  const rows = [
    ...WELL_KNOWN_FLAGS.map((wk) => {
      const db = dbByKey.get(wk.key);
      return {
        key: wk.key,
        value: db?.value ?? true,
        description: db?.description ?? wk.description,
        updatedAt: db?.updatedAt ?? null,
        isWellKnown: true,
        isOverridden: db !== undefined,
      };
    }),
    ...(flags
      ?.filter((f) => !knownKeys.has(f.key))
      .map((f) => ({
        key: f.key,
        value: f.value,
        description: f.description,
        updatedAt: f.updatedAt,
        isWellKnown: false,
        isOverridden: true,
      })) ?? []),
  ];

  return (
    <main className="wide">
      <div className="card">
        <FounderNav />
        <h1>Feature flags</h1>
        <p className="muted">
          Emergency stops + dark-ship toggles. Defaults are <strong>true</strong> — flipping a flag
          to <strong>false</strong> disables that part of the system. Changes propagate within ~5s.
        </p>
        {error && <p className="error">{error}</p>}
        {!flags && !error && <p className="muted">Loading…</p>}

        {flags && (
          <ul className="flag-list">
            {rows.map((r) => (
              <li key={r.key} className="flag-row">
                <div className="flag-row-head">
                  <strong>{r.key}</strong>
                  {!r.isWellKnown && <span className="muted">custom</span>}
                  {r.isWellKnown && !r.isOverridden && <span className="muted">default</span>}
                  <span className={`status-pill status-${r.value ? 'active' : 'banned'}`}>
                    {r.value ? 'enabled' : 'DISABLED'}
                  </span>
                </div>
                {r.description && <p className="muted flag-row-desc">{r.description}</p>}
                {r.updatedAt && (
                  <p className="muted flag-row-meta">
                    last changed {new Date(r.updatedAt).toLocaleString()}
                  </p>
                )}
                <div className="flag-row-action">
                  <button
                    type="button"
                    disabled={pending === r.key}
                    onClick={() => void toggle(r.key, r.value, r.description)}
                    className={r.value ? 'secondary danger' : ''}
                  >
                    {pending === r.key ? 'Saving…' : r.value ? 'Disable' : 'Enable'}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <section className="flag-new">
          <h2>Add a new flag</h2>
          <p className="muted">
            New flags are only useful if code reads them. Add a getFlag('your_key', defaultValue)
            call somewhere first.
          </p>
          <div className="flag-new-form">
            <input
              type="text"
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              placeholder="enable_new_feature"
              aria-label="Flag key"
            />
            <input
              type="text"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="What does this flag control?"
              aria-label="Flag description"
            />
            <button
              type="button"
              disabled={!newKey.trim() || pending === newKey.trim()}
              onClick={create}
            >
              Create flag (enabled)
            </button>
          </div>
        </section>
      </div>
    </main>
  );
}
